import {
  createSubmitCancelLifecycleContract,
  defineDoctype,
  type DoctypeCatalogMetadata,
  type DoctypeChildCascadePolicy,
  type DoctypeDefinition,
} from "@kubdee/sdk";
import { purchaseDebitNotes, purchaseInvoices } from "./schemas/invoices";
import { purchaseOrderItems } from "./schemas/purchase-order-items";
import { purchaseOrders } from "./schemas/purchase-orders";
import { purchaseSuppliers } from "./schemas/purchase-suppliers";
import { supplierContacts } from "./schemas/supplier-contacts";
import {
  purchaseLandedCostAllocations,
  purchaseLandedCostLines,
  purchaseLandedCosts,
  purchaseMatchingPolicies,
} from "./schemas/procurement-control";

// ============================================================================
// Purchase module — registered doctypes.
//
// Doctype Admin Epic (#347): #413 (supplier) + #422 (purchase_order,
// purchase_invoice, debit_note).
//
// This file is imported by `modules/purchase/index.ts` and re-exported as
// `registeredDoctypes` on the module manifest. The generic platform boot
// loop in `apps/tenant-worker/src/middleware/auto-migrate.ts` reads
// `module.registeredDoctypes` across every enabled module and upserts
// platform-default rows into `core_doctype_registry` — no per-module glue
// required, so future purchase doctypes only need to append to
// `purchaseDoctypes` below.
//
// Scope:
//   - `supplier` (#413): master-data doctype symmetric to sales' `customer`.
//     Stored in the purchase-owned `purchase_suppliers` table. Legacy rows
//     are backfilled from `accounting_partners where is_supplier = 1`.
//   - `purchase_order` (#422): transactional doctype owned by purchase.
//   - `purchase_invoice` + `debit_note`: legacy split-table metadata is kept
//     below for compatibility with old tenants/migrations, but these doctypes
//     are no longer registered by Purchase. Accounting owns the canonical
//     invoice/note doctypes and tables; Purchase only keeps workflow routes.
// ============================================================================

export const purchaseOrderLifecycle = createSubmitCancelLifecycleContract({
  docStatusField: null,
  submittedStates: ["submitted", "approved", "partially_received", "received", "partially_billed", "invoiced"],
  cancelFromStates: ["draft", "submitted", "approved", "partially_received", "received", "partially_billed"],
  submitDocStatus: null,
  cancelDocStatus: null,
});

export const purchaseInvoiceLifecycle = createSubmitCancelLifecycleContract({
  submittedStates: ["submitted", "partially_paid", "paid", "overdue"],
  cancelFromStates: ["draft", "submitted", "partially_paid", "paid", "overdue"],
  reversalPolicy: "reverse_gl",
});

export const landedCostLifecycle = createSubmitCancelLifecycleContract({
  submittedStates: ["submitted"],
  cancelFromStates: ["draft", "submitted"],
  reversalPolicy: "reverse_gl",
});

const MASTER_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const DOCUMENT_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const CHILD_TABLE_CATALOG = (
  parentDoctype: string,
  parentField: string,
  parentKeyField: string,
  sortField?: string,
  cascadePolicy: DoctypeChildCascadePolicy = "restrict",
): DoctypeCatalogMetadata => ({
  childTable: {
    parentDoctype,
    parentField,
    parentKeyField,
    sortField: sortField ?? null,
    cascadePolicy,
  },
  policy: {
    isExportable: true,
    isReportable: true,
  },
});

const JUNCTION_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const SYSTEM_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

/**
 * Supplier (`ผู้จำหน่าย`) — the master-data pilot on the purchase side,
 * symmetric to CRM's `customer` doctype (see `modules/crm/doctypes.ts`).
 *
 * Stored in the purchase-owned `purchase_suppliers` table. The schema keeps
 * the partner-shaped columns (`partner_code`, `partner_name`, role flags)
 * so the existing purchase supplier API response shape remains stable while
 * ownership moves out of `accounting_partners`.
 *
 * `defaultSeries` seeds `core_numbering_series` with
 * `(module=purchase, doctype=supplier, company_id=NULL)` on tenant boot —
 * matches the pair the live create path uses in
 * `modules/purchase/routes/suppliers.ts`.
 */
export const supplierDoctype = defineDoctype({
  id: "supplier",
  moduleId: "purchase",
  table: purchaseSuppliers,
  label: "ผู้จำหน่าย",
  icon: "Users",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  // Supplier id surfaces on list/detail views as `partner_code` (e.g.
  // `S00001`). The live create path routes the generated number into that
  // column on the purchase-owned supplier row.
  numberingField: "partner_code",
  displayField: "partner_name",
  imageField: null,
  searchFields: ["partner_name", "partner_code"],
  // Thai UI copy for the partner columns surfaced in supplier views.
  // System/audit columns (company_id, timestamps, deleted_*) are omitted
  // because admins don't see them in list/form views.
  columnLabels: {
    partner_code: "รหัสผู้จำหน่าย",
    partner_name: "ชื่อผู้จำหน่าย",
    custom_data: "ข้อมูลเพิ่มเติม",
  },
  // Platform-default numbering series — seeded into `core_numbering_series`
  // with `(module=purchase, doctype=supplier, company_id=NULL)` on module
  // boot so a fresh tenant can create a supplier without pre-configuring a
  // series. Admins override per-company through the Doctype Admin Naming
  // tab. Matches the pair at `modules/purchase/routes/suppliers.ts:186`.
  defaultSeries: {
    prefix: "S-#####",
    resetEvery: "never",
    seriesName: "Standard",
  },
});

export const supplierContactDoctype = defineDoctype({
  id: "supplier_contact",
  moduleId: "purchase",
  table: supplierContacts,
  label: "ช่องทางติดต่อผู้จำหน่าย",
  icon: "Contact",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("supplier", "contacts", "supplier_id"),
  numberingField: null,
  displayField: "contact_value",
  imageField: null,
  searchFields: ["contact_value"],
  columnLabels: {
    supplier_id: "ผู้จำหน่าย",
    channel_id: "ช่องทาง",
    contact_value: "ข้อมูลติดต่อ",
    is_primary: "ช่องทางหลัก",
    notes: "หมายเหตุ",
  },
});

/**
 * Purchase Order (`ใบสั่งซื้อ`) — transactional doctype owned by purchase.
 *
 * Stored in the purchase-owned `purchase_orders` table; the live create
 * path is `modules/purchase/routes/purchase-orders.ts:182` which calls
 * `generateNextNumber({ module: PURCHASE, doctype: "purchase_order" })`.
 *
 * `defaultSeries.prefix` mirrors the legacy `seed.ts` value so existing
 * per-company rows keep their running counter on migration; fresh
 * tenants resolve through the platform-default fallback.
 */
export const purchaseOrderDoctype = defineDoctype({
  id: "purchase_order",
  moduleId: "purchase",
  table: purchaseOrders,
  label: "ใบสั่งซื้อ",
  icon: "FileText",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "po_no",
  displayField: "po_no",
  imageField: null,
  searchFields: ["po_no", "status"],
  columnLabels: {
    po_no: "เลขที่ใบสั่งซื้อ",
    branch_id: "สาขา",
    party_type: "ประเภทคู่ค้า",
    party_id: "ผู้จำหน่าย",
    po_date: "วันที่",
    expiry_date: "วันหมดอายุ",
    subtotal: "ยอดก่อนภาษี",
    tax_amount: "ภาษี",
    total: "ยอดรวม",
    status: "สถานะ",
    remarks: "หมายเหตุ",
    converted_to_invoice_id: "ใบแจ้งหนี้ที่เชื่อมโยง",
  },
  defaultSeries: {
    prefix: "PO-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: purchaseOrderLifecycle,
});

/**
 * Purchase Invoice (`ใบแจ้งหนี้ซื้อ`) — legacy compatibility metadata.
 *
 * The old source table is archived under `purchase_legacy_invoices_archive`
 * for tenant history/backfill safety, but Purchase no longer registers this
 * doctype. Live create/update paths delegate to Accounting and use the
 * Accounting-owned
 * `(MODULES.ACCOUNTING, "purchase_invoice")` numbering pair.
 */
export const purchaseInvoiceDoctype = defineDoctype({
  id: "purchase_invoice",
  moduleId: "purchase",
  table: purchaseInvoices,
  label: "ใบแจ้งหนี้ซื้อ",
  icon: "FileText",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "invoice_no",
  displayField: "invoice_no",
  imageField: null,
  searchFields: ["invoice_no", "status"],
  columnLabels: {
    invoice_no: "เลขที่ใบแจ้งหนี้",
    branch_id: "สาขา",
    party_type: "ประเภทคู่ค้า",
    party_id: "ผู้จำหน่าย",
    invoice_date: "วันที่",
    due_date: "วันครบกำหนด",
    subtotal: "ยอดก่อนภาษี",
    tax_amount: "ภาษี",
    total: "ยอดรวม",
    outstanding: "ค้างชำระ",
    status: "สถานะ",
    remarks: "หมายเหตุ",
  },
  defaultSeries: {
    prefix: "PINV-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: purchaseInvoiceLifecycle,
});

/**
 * Debit Note (`ใบเพิ่มหนี้`) — legacy compatibility metadata.
 *
 * The old source table is archived under
 * `purchase_legacy_debit_notes_archive` for tenant history/backfill safety,
 * but Purchase no longer registers this doctype. Accounting owns the canonical
 * supplier debit-note workflow; Purchase keeps a route-level compatibility
 * surface for procurement users.
 */
export const debitNoteDoctype = defineDoctype({
  id: "debit_note",
  moduleId: "purchase",
  table: purchaseDebitNotes,
  label: "ใบเพิ่มหนี้",
  icon: "FilePlus",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "invoice_no",
  displayField: "invoice_no",
  imageField: null,
  searchFields: ["invoice_no", "status"],
  columnLabels: {
    invoice_no: "เลขที่ใบเพิ่มหนี้",
    branch_id: "สาขา",
    party_id: "ผู้จำหน่าย",
    invoice_date: "วันที่",
    debit_note_of: "ใบแจ้งหนี้อ้างอิง",
    reason_code: "เหตุผล",
    original_invoice_amount: "ยอดเดิม",
    corrected_amount: "ยอดหลังปรับ",
    difference_amount: "ยอดต่าง",
    total: "ยอดรวม",
    status: "สถานะ",
    remarks: "หมายเหตุ",
  },
  defaultSeries: {
    prefix: "DN-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: purchaseInvoiceLifecycle,
});

export const landedCostDoctype = defineDoctype({
  id: "landed_cost",
  moduleId: "purchase",
  table: purchaseLandedCosts,
  label: "ต้นทุนแฝง",
  icon: "Ship",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "landed_cost_no",
  displayField: "landed_cost_no",
  imageField: null,
  searchFields: ["landed_cost_no", "status"],
  columnLabels: {
    landed_cost_no: "เลขที่ต้นทุนแฝง",
    branch_id: "สาขา",
    posting_date: "วันที่บันทึก",
    allocation_method: "วิธีปันส่วน",
    clearing_account_id: "บัญชีพักค่าใช้จ่าย",
    total_amount: "ยอดรวม",
    status: "สถานะ",
    remarks: "หมายเหตุ",
  },
  defaultSeries: {
    prefix: "LC-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: landedCostLifecycle,
});

export const purchaseOrderItemDoctype = defineDoctype({
  id: "purchase_order_item",
  moduleId: "purchase",
  table: purchaseOrderItems,
  label: "รายการในใบสั่งซื้อ",
  icon: "Rows3",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("purchase_order", "items", "purchase_order_id", "sort_order"),
  numberingField: null,
  displayField: "description",
  imageField: null,
  searchFields: ["item_id", "description"],
  columnLabels: {
    purchase_order_id: "ใบสั่งซื้อ",
    item_id: "สินค้า",
    description: "รายละเอียด",
    qty: "จำนวน",
    rate: "ราคา/หน่วย",
    amount: "มูลค่ารวม",
    received_qty: "จำนวนที่รับแล้ว",
    received_amount: "มูลค่าที่รับแล้ว",
    billed_qty: "จำนวนที่วางบิลแล้ว",
    billed_amount: "มูลค่าที่วางบิลแล้ว",
    account_id: "บัญชี",
    tax_template_id: "รูปแบบภาษี",
    tax_amount: "ภาษี",
    sort_order: "ลำดับ",
  },
});

export const purchaseMatchingPolicyDoctype = defineDoctype({
  id: "purchase_matching_policy",
  moduleId: "purchase",
  table: purchaseMatchingPolicies,
  label: "นโยบายจับคู่จัดซื้อ",
  icon: "Settings",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "system",
  catalog: SYSTEM_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "over_receipt_action", "over_billing_action"],
});

export const landedCostLineDoctype = defineDoctype({
  id: "landed_cost_line",
  moduleId: "purchase",
  table: purchaseLandedCostLines,
  label: "รายการต้นทุนแฝง",
  icon: "Rows3",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("landed_cost", "charges", "landed_cost_id", "sort_order"),
  numberingField: null,
  displayField: "charge_type",
  imageField: null,
  searchFields: ["charge_type", "description"],
  columnLabels: {
    landed_cost_id: "ต้นทุนแฝง",
    charge_type: "ประเภทค่าใช้จ่าย",
    description: "รายละเอียด",
    amount: "มูลค่า",
    sort_order: "ลำดับ",
  },
});

export const landedCostAllocationDoctype = defineDoctype({
  id: "landed_cost_allocation",
  moduleId: "purchase",
  table: purchaseLandedCostAllocations,
  label: "การปันส่วนต้นทุนแฝง",
  icon: "Network",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "junction",
  catalog: JUNCTION_CATALOG,
  numberingField: null,
  displayField: "goods_receipt_item_id",
  imageField: null,
  searchFields: ["landed_cost_id", "goods_receipt_id", "goods_receipt_item_id", "item_id", "warehouse_id"],
  columnLabels: {
    landed_cost_id: "ต้นทุนแฝง",
    goods_receipt_id: "ใบรับสินค้า",
    goods_receipt_item_id: "รายการใบรับสินค้า",
    item_id: "สินค้า",
    warehouse_id: "คลังสินค้า",
    base_amount: "ฐานปันส่วน",
    allocated_amount: "มูลค่าที่ปันส่วน",
  },
});

/**
 * Every doctype the purchase module wants registered with the platform.
 * The order here is preserved in the Admin UI through `listDoctypes()` sort.
 */
export const purchaseDoctypes: readonly DoctypeDefinition[] = Object.freeze([
  supplierDoctype,
  supplierContactDoctype,
  purchaseMatchingPolicyDoctype,
  purchaseOrderDoctype,
  purchaseOrderItemDoctype,
  landedCostDoctype,
  landedCostLineDoctype,
  landedCostAllocationDoctype,
]);
